/**
 * ViewInstruccionesButton — Iter 95 (Feb 2026)
 *
 * Botón que el montador (o cualquier usuario) pulsa para ver:
 *   - Foto previa / render de cómo debería quedar el montaje
 *   - Documentos adjuntos de la ruta asociados a esa línea
 *   - Documentos globales de la ruta (instrucciones generales)
 *   - Notas técnicas
 *
 * Props:
 *   - lineaId (str, requerido): id de la línea de trabajo
 *   - size (str): "sm" (default) | "xs"
 *   - variant (str): "outline" (default) | "ghost"
 *   - label (str): texto. Si no, "Ver instrucciones"
 *   - iconOnly (bool): solo icono
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Image as ImageIcon, FileText, Eye, Loader2, ExternalLink, Download, Building2 } from "lucide-react";
import { toast } from "sonner";

function bytesPretty(n) {
  if (!n) return "";
  if (n > 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n > 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

function isImage(url, mime) {
  if (mime?.startsWith("image/")) return true;
  if (!url) return false;
  return /\.(png|jpe?g|gif|webp|heic|heif)(\?|$)/i.test(url);
}

export function ViewInstruccionesButton({
  lineaId,
  size = "sm",
  variant = "outline",
  label,
  iconOnly = false,
  testId = "view-instr-btn",
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/lineas/${lineaId}/instrucciones`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar las instrucciones");
      setOpen(false);
    } finally { setLoading(false); }
  };

  const onOpenChange = (o) => {
    setOpen(o);
    if (o) load();
    else setData(null);
  };

  if (!lineaId) return null;

  const totalAdj = data ? (data.adjuntos_linea?.length || 0) + (data.adjuntos_globales?.length || 0) : 0;
  const tieneFoto = !!data?.linea?.foto_previa?.url;

  return (
    <>
      <Button
        type="button"
        size={size === "xs" ? "sm" : size}
        variant={variant}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        data-testid={testId}
        title="Ver instrucciones y documentos del montaje"
        className={size === "xs" ? "h-7 text-[11px] px-2" : ""}
      >
        <Eye className={iconOnly ? "w-3.5 h-3.5" : "w-3.5 h-3.5 mr-1.5"} />
        {!iconOnly && (label || "Ver instrucciones")}
      </Button>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-zinc-950 border-amber-500/40 max-w-3xl max-h-[90vh] overflow-y-auto"
                       data-testid="view-instrucciones-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-200 flex items-center gap-2">
              <Eye className="w-5 h-5" /> Instrucciones de montaje
            </DialogTitle>
            {data?.cliente && (
              <DialogDescription className="text-xs text-zinc-400 pt-1 flex items-center gap-2 flex-wrap">
                <Building2 className="w-3 h-3" />
                <span className="text-zinc-200">{data.cliente.razon_social || data.cliente.nombre}</span>
                {data.peticion?.codigo && <span className="text-amber-300 font-mono">[{data.peticion.codigo}]</span>}
                {data.espacio?.nombre && <span>· 📍 {data.espacio.nombre}</span>}
              </DialogDescription>
            )}
          </DialogHeader>

          {loading && (
            <div className="flex items-center justify-center py-10 text-zinc-500 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Cargando…
            </div>
          )}

          {!loading && data && (
            <div className="space-y-4">
              {/* Descripción + notas */}
              {(data.linea.descripcion || data.linea.notas_montaje) && (
                <div className="bg-zinc-900/60 border border-zinc-800 rounded p-3">
                  {data.linea.descripcion && (
                    <div className="text-sm text-zinc-100 mb-1">
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-2">Descripción</span>
                      {data.linea.descripcion}
                    </div>
                  )}
                  {data.linea.notas_montaje && (
                    <div className="text-sm text-amber-200 mt-2 whitespace-pre-wrap">
                      <span className="text-[10px] uppercase tracking-wider text-amber-400 mr-2">🛠️ Notas técnicas</span>
                      {data.linea.notas_montaje}
                    </div>
                  )}
                </div>
              )}

              {/* Foto previa / render */}
              {tieneFoto ? (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1.5 flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" /> Foto previa / render
                  </div>
                  <a href={data.linea.foto_previa.url} target="_blank" rel="noreferrer"
                     data-testid="view-instr-foto-previa">
                    <img src={data.linea.foto_previa.url} alt="Foto previa montaje"
                         className="w-full max-h-96 object-contain rounded border border-zinc-800 bg-zinc-900" />
                  </a>
                  {data.linea.foto_previa.caption && (
                    <div className="text-xs text-zinc-400 mt-1 italic">{data.linea.foto_previa.caption}</div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-zinc-500 italic">Sin foto previa subida.</div>
              )}

              {/* Adjuntos específicos */}
              {data.adjuntos_linea?.length > 0 && (
                <AdjuntosList
                  title="📌 Documentos específicos de esta parada"
                  items={data.adjuntos_linea}
                  testIdPrefix="adj-linea"
                />
              )}

              {/* Adjuntos globales */}
              {data.adjuntos_globales?.length > 0 && (
                <AdjuntosList
                  title="📋 Documentos generales de la ruta"
                  items={data.adjuntos_globales}
                  testIdPrefix="adj-global"
                />
              )}

              {totalAdj === 0 && !tieneFoto && !data.linea.notas_montaje && !data.linea.descripcion && (
                <div className="bg-zinc-900/40 border border-zinc-800 rounded p-6 text-center text-zinc-500 text-sm">
                  📭 No hay material adicional para este montaje todavía.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function AdjuntosList({ title, items, testIdPrefix }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-amber-400 mb-1.5">{title} ({items.length})</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map((a, idx) => {
          const img = isImage(a.url, a.mime_type);
          return (
            <a key={a.id || idx} href={a.url} target="_blank" rel="noreferrer"
               data-testid={`${testIdPrefix}-${idx}`}
               className="block bg-zinc-900/60 border border-zinc-800 rounded p-2 hover:border-amber-500/40 transition-colors">
              {img ? (
                <img src={a.url} alt={a.filename || "adjunto"}
                     className="w-full h-32 object-cover rounded mb-2 bg-zinc-950" />
              ) : (
                <div className="w-full h-32 flex items-center justify-center bg-zinc-950 rounded mb-2">
                  <FileText className="w-10 h-10 text-zinc-600" />
                </div>
              )}
              <div className="text-xs text-zinc-100 truncate flex items-center gap-1">
                {img ? <ImageIcon className="w-3 h-3 text-emerald-400" /> : <FileText className="w-3 h-3 text-blue-400" />}
                {a.filename || a.titulo || "documento"}
              </div>
              <div className="text-[10px] text-zinc-500 flex items-center gap-2 mt-0.5">
                {a.size_bytes && <span>{bytesPretty(a.size_bytes)}</span>}
                {a.ruta_codigo && <span className="text-amber-300/70 font-mono">[{a.ruta_codigo}]</span>}
                <span className="ml-auto text-cyan-400 flex items-center gap-0.5">
                  Abrir <ExternalLink className="w-2.5 h-2.5" />
                </span>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}

export default ViewInstruccionesButton;
